import { v4 as uuidv4 } from "uuid";
import { geminiRequest, readJsonFile } from "..";
import i18n from "./i18n";

export const handleDownloadJson = (values: any, name = "survey-data") => {
  const jsonString = JSON.stringify(values, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const link = document.createElement("a");
  link.download = `${name}.json`;
  link.href = window.URL.createObjectURL(blob);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  return Promise.resolve();
};

// export function handleExtractUIDs(json) {
//   const uidFields = ["uid", "goToPage", "response"];
//   const uidMappings = new Map();

//   function processObject(obj) {
//     if (!obj || typeof obj !== "object") return; // Added to handle null or non-object

//     Object.entries(obj).forEach(([key, value]) => {
//       if (uidFields.includes(key) && typeof value === "string") {
//         const originalUID = value;
//         if (!uidMappings.has(originalUID)) {
//           // Only generate new UID if not already mapped
//           const newUID = uuidv4();
//           uidMappings.set(originalUID, newUID);
//         }
//         obj[key] = uidMappings.get(originalUID);
//       } else if (typeof value === "object") {
//         processObject(value); // Recursion for objects and arrays
//       }
//     });
//   }

//   processObject(json);
//   return uidMappings;
// }

// export function handleExtractUIDs(json) {
//   const uidFields = ["uid", "goToPage", "response"];
//   const uidMappings = new Map();
//   function processObject(obj) {
//     for (const key in obj) {
//       if (uidFields.includes(key)) {
//         const originalUID = obj[key];
//         const newUID = uuidv4();
//         obj[key] = newUID;
//         uidMappings.set(originalUID, newUID);
//       } else if (typeof obj[key] === "object" || Array.isArray(obj[key])) {
//         processObject(obj[key]);
//       }
//     }
//   }

//   processObject(json);
//   return uidMappings;
// }

export function handleExtractUIDs(json: any) {
  const uidFields = ["uid", "goToPage", "response"];
  const uidMappings = new Map<string, string>();

  function processObject(obj: any) {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return;

    for (const [key, value] of Object.entries(obj)) {
      if (uidFields.includes(key) && typeof value === "string") {
        if (!uidMappings.has(value)) {
          uidMappings.set(value, uuidv4());
        }
        obj[key] = uidMappings.get(value);
      } else if (typeof value === "object") {
        processObject(value);
      }
    }
  }

  function processArray(arr: any[]) {
    arr.forEach((item) => {
      if (typeof item === "object") {
        processObject(item);
      }
    });
  }

  if (Array.isArray(json)) {
    processArray(json);
  } else {
    processObject(json);
  }

  return uidMappings;
}

export function handleReinsertUIDs(
  json: any,
  uidMappings: Map<string, string>,
) {
  function processObject(obj: any) {
    for (const key in obj) {
      if (typeof obj[key] === "string" && uidMappings.has(obj[key])) {
        obj[key] = uidMappings.get(obj[key]);
      } else if (obj[key] && typeof obj[key] === "object") {
        processObject(obj[key]);
      }
    }
  }

  // Clone the json object to avoid direct modification
  const newJson = JSON.parse(JSON.stringify(json));
  processObject(newJson);
  return newJson;
}

export function cleanAndParseAiJson<
  T extends string | string[] | Record<string, any>,
>(jsonString: string | any): T | undefined {
  if (typeof jsonString !== "string") {
    return jsonString as T;
  }

  let str = jsonString.trim();

  // Strip markdown code fences: ```json ... ```, ```JSON ... ```, ``` ... ```
  const fenceMatch = str.match(/^```(?:json|JSON)?\s*\r?\n?([\s\S]*?)\r?\n?\s*```\s*$/);
  if (fenceMatch) {
    str = fenceMatch[1].trim();
  }

  try {
    return JSON.parse(str);
  } catch (_error) {
    return jsonString as T;
  }
}

export function createJsonChunks(
  data: Record<string, string>,
  maxChunkSize = 15000,
  maxKeys = Infinity,
): Record<string, string>[] {
  const chunks: Record<string, string>[] = [];
  let currentChunk: Record<string, string> = {};
  let currentChunkSize = 0;
  let currentKeyCount = 0;

  for (const [id, text] of Object.entries(data)) {
    const itemSize = text.length;
    if (
      (currentChunkSize + itemSize > maxChunkSize || currentKeyCount >= maxKeys) &&
      currentKeyCount > 0
    ) {
      chunks.push(currentChunk);
      currentChunk = {};
      currentChunkSize = 0;
      currentKeyCount = 0;
    }
    currentChunk[id] = text;
    currentChunkSize += itemSize;
    currentKeyCount++;
  }

  if (currentKeyCount > 0) {
    chunks.push(currentChunk);
  }

  return chunks;
}

export function extractReinsertableContent(
  json: Record<string, any>,
  includedKeys: string[],
): [string, string][] {
  const reinsertableContent: [string, string][] = [];
  let index = 0;

  function processObject(obj: Record<string, any>) {
    for (const key in obj) {
      if (
        !includedKeys.includes(key) &&
        isNaN(Number(key)) &&
        !Array.isArray(obj[key])
      ) {
        continue;
      }

      if (obj[key] === "" || obj[key] === null) {
        continue;
      }

      if (typeof obj[key] === "string") {
        const id = `reinsertionId-${index}`;
        reinsertableContent.push([id, obj[key]]);
        obj[key] = id;
        index++;
      } else if (typeof obj[key] === "object") {
        console.log(obj[key]);
        processObject(obj[key]);
      }
    }
  }

  processObject(json);
  return reinsertableContent;
}

export function reinsertReinsertableContent(
  json: Record<string, any>,
  translations: Record<string, string>,
): Record<string, any> {
  function processObject(obj: Record<string, any>) {
    for (const key in obj) {
      if (
        typeof obj[key] === "string" &&
        obj[key].startsWith("reinsertionId-")
      ) {
        const translated = translations[obj[key]];
        if (translated) {
          obj[key] = translated;
        }
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        processObject(obj[key]);
      }
    }
  }

  processObject(json);
  return json;
}

export const handleAiTransformData = async (
  assistantId: string,
  inputPrompt: string,
  includedKeys: string[],
  toBeAiTransformedJSON: Record<string, any>,
  handleSubmit: (data: Record<string, any>) => void,
  setLoading: (loading: boolean) => void,
): Promise<void> => {
  const overallT0 = performance.now();
  setLoading(true);
  const aiTransformableStringsArray = extractReinsertableContent(
    toBeAiTransformedJSON,
    includedKeys,
  );
  const aiTransformableStringsObject = Object.fromEntries(
    aiTransformableStringsArray,
  );

  const totalChars = Object.values(aiTransformableStringsObject).reduce((s, v) => s + v.length, 0);
  console.log(`[handleAiTransformData] ${Object.keys(aiTransformableStringsObject).length} keys, ${totalChars} chars`);

  if (Object.keys(aiTransformableStringsObject).length === 0) {
    handleSubmit({ ...toBeAiTransformedJSON, createdAt: new Date().toISOString() });
    setLoading(false);
    return;
  }

  const jsonChunks = createJsonChunks(aiTransformableStringsObject);
  console.log(`[handleAiTransformData] split into ${jsonChunks.length} chunk(s)`);

  try {
    const chunkResults = await Promise.all(
      jsonChunks.map(async (chunk, i) => {
        const chunkChars = Object.values(chunk).reduce((s, v) => s + v.length, 0);
        console.log(`[handleAiTransformData] chunk ${i + 1}/${jsonChunks.length}: ${Object.keys(chunk).length} keys, ${chunkChars} chars — sending…`);
        const ct0 = performance.now();

        const prompt = `${inputPrompt}. Please translate the values of the following JSON object, returning a valid JSON object with the identical keys. Do not alter the keys or the structure of the JSON. Only translate the string values:\n\n${JSON.stringify(
          chunk,
          null,
          2,
        )}`;

        let retryCount = 0;
        const maxRetries = 4;

        while (retryCount < maxRetries) {
          const at0 = performance.now();
          const result = await geminiRequest({
            body: prompt,
            assistantId: assistantId,
            lang: i18n?.resolvedLanguage || "en",
          });
          const atMs = Math.round(performance.now() - at0);

          if (result) {
            const parsed = cleanAndParseAiJson(result);

            if (
              typeof parsed === "object" &&
              parsed !== null &&
              !Array.isArray(parsed)
            ) {
              const originalKeys = Object.keys(chunk);
              const parsedKeys = Object.keys(parsed as Record<string, string>);
              if (
                originalKeys.length === parsedKeys.length &&
                originalKeys.every((key) => parsedKeys.includes(key))
              ) {
                console.log(`[handleAiTransformData] chunk ${i + 1} ✓ attempt ${retryCount + 1} in ${atMs}ms`);
                return parsed as Record<string, string>;
              } else {
                const missing = originalKeys.filter((k) => !parsedKeys.includes(k));
                console.warn(
                  `[handleAiTransformData] chunk ${i + 1} attempt ${retryCount + 1}: mismatched keys (missing: ${missing.join(", ")}), ${atMs}ms — retrying`,
                );
                retryCount++;
              }
            } else {
              console.warn(`[handleAiTransformData] chunk ${i + 1} attempt ${retryCount + 1}: not a valid JSON object, ${atMs}ms — retrying`);
              retryCount++;
            }
          } else {
            console.warn(`[handleAiTransformData] chunk ${i + 1} attempt ${retryCount + 1}: empty response, ${atMs}ms — retrying`);
            retryCount++;
          }
        }

        console.error(`[handleAiTransformData] chunk ${i + 1} FAILED after ${maxRetries} attempts (${Math.round(performance.now() - ct0)}ms) — using original`);
        return chunk;
      }),
    );

    const allTranslations: Record<string, string> = {};
    for (const r of chunkResults) Object.assign(allTranslations, r);

    const aiTransformedData = reinsertReinsertableContent(
      toBeAiTransformedJSON,
      allTranslations,
    );

    const newSurvey = {
      ...aiTransformedData,
      createdAt: new Date().toISOString(),
    };

    console.log(`[handleAiTransformData] done in ${Math.round(performance.now() - overallT0)}ms`);
    handleSubmit(newSurvey);
    setLoading(false);
  } catch (error) {
    setLoading(false);
    console.error("Translation error:", error, `(after ${Math.round(performance.now() - overallT0)}ms)`);
  }
};

export function handleGetFieldsToInclude(variant: "survey" | "infoPage" | "") {
  return variant === "survey"
    ? ["title", "callToParticipate", "pages", "createdAt"]
    : variant === "infoPage"
      ? ["title", "brief", "pages", "image", "createdAt"]
      : [];
}
export async function handleInsertJSONTemplate(
  files: FileList | File[] | null,
  data: any,
  variant: "survey" | "infoPage" | "",
) {
  const fieldsToInclude = handleGetFieldsToInclude(variant);

  let readJsonData = data ? await data : null;

  if (!readJsonData) {
    const file = files?.[0];
    const fileName = file?.name?.toLowerCase() || "";
    const fileType = file?.type?.toLowerCase() || "";
    const isJsonFile =
      fileType === "application/json" || fileName.endsWith(".json");

    if (file && isJsonFile) {
      readJsonData = await readJsonFile(file);
    } else {
      return;
    }
  }
  if (!readJsonData) return;

  console.log(variant, fieldsToInclude);
  // Filter jsonData to include only the specified fields
  const filteredData = fieldsToInclude.reduce(
    (acc: Record<string, any>, field) => {
      if (readJsonData[field] !== undefined) {
        acc[field] = readJsonData[field];
      }
      return acc;
    },
    {},
  );

  // Extract UIDs and replace them
  const uidMappings = handleExtractUIDs(filteredData);
  const newData = handleReinsertUIDs(filteredData, uidMappings);

  // Prepare updated data for Formik, preserving specific values
  const updatedFormikValues = {
    ...newData,
    title: `${newData?.title} (${i18n.t("global:copied")})`,
    createdAt: new Date().toISOString(),
  };

  return updatedFormikValues;
}
